import React from "react";
import {
  Grid,
  Typography,
  Autocomplete,
  Box,
  TextField,
} from "@mui/material";
import { UserOptionDisplay } from '@/__core/components';

import TextareaAutosize from 'react-textarea-autosize';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LabeledField } from "@/__core/components/LabeledField";
import { useSelector } from "react-redux";
import { getEmployeeByUser, getEmployees } from "@/modules/employees/redux/EmployeeCRUD";
import { getCurrentCompanyId } from "@/modules/auth/helpers/CompanyStorage";
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';

const currentEmployeeByUserCache = new Map<string, any | null>();
const currentEmployeeByUserInFlight = new Map<string, Promise<any | null>>();

const normalizeMissionCode = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim().toUpperCase();
  }

  if (value && typeof value === 'object') {
    const missionObject = value as { code?: unknown; mission?: unknown; value?: unknown };
    return normalizeMissionCode(missionObject.code || missionObject.mission || missionObject.value || '');
  }

  return '';
};

const employeeHasMission = (employee: any, expectedMission: string): boolean => {
  const expected = normalizeMissionCode(expectedMission);
  if (!expected) {
    return false;
  }

  const missions = Array.isArray(employee?.missions)
    ? employee.missions
    : Array.isArray(employee?.mission)
      ? employee.mission
      : employee?.mission
        ? [employee.mission]
        : [];

  return missions.some((item: unknown) => normalizeMissionCode(item) === expected);
};

const filterEmployeesByMission = (employees: any[], missionCode: string) => {
  if (!Array.isArray(employees) || employees.length === 0) {
    return [];
  }

  // Some API variants already pre-filter by mission and only return minimal fields
  // (id, name, nickname) without missions metadata. In this case, keep the payload.
  const hasMissionMetadata = employees.some((employee) =>
    employee?.mission !== undefined || employee?.missions !== undefined
  );

  if (!hasMissionMetadata) {
    return employees;
  }

  return employees.filter((employee) => employeeHasMission(employee, missionCode));
};

type MissionEmployeesPayload = {
  sales: Array<{ id: string; name: string; nickname?: string; avatar?: string | null; image1?: string | null; user?: { id?: string; name?: string }; user_id?: string }>;
  survey: Array<{ id: string; name: string; nickname?: string; avatar?: string | null; image1?: string | null; user?: { id?: string; name?: string }; user_id?: string }>;
  cnc: Array<{ id: string; name: string; nickname?: string; avatar?: string | null; image1?: string | null; user?: { id?: string; name?: string }; user_id?: string }>;
};
const missionEmployeesCache = new Map<string, MissionEmployeesPayload>();
const missionEmployeesInFlight = new Map<string, Promise<MissionEmployeesPayload>>();

// Employee autocomplete with UserOptionDisplay


interface OrderStaffProps {
  isEditMode?: boolean;
  formData: {
    company_id?: string;
    sales_employee_id: string;
    delivery_date: Date | null;
    survey_id?: string;
    survey_notes?: string;
    survey_appointment_date?: Date | null;
    survey_appointment_date_text?: string;
    survey_date?: Date | null;
    survey_date_text?: string;
    tech_cnc_id?: string;
    export_file_date?: Date | null;
    export_file_date_text?: string;
  };
  employees: Array<{ 
    id: string; 
    name: string; 
    code?: string;
    user?: {
      id?: string;
      name?: string;
    };
    first_name?: string;
    last_name?: string;
    company_name?: string;
    company?: { 
      id: string; 
      name: string; 
      code: string; 
    }; 
  }>;
  disabled?: boolean;
  onInputChange: (field: string, value: any) => void;
}

export default function OrderStaff({
  isEditMode = false,
  formData,
  employees,
  disabled = false,
  onInputChange,
}: OrderStaffProps) {
  const getBlurInputValue = React.useCallback((event: React.FocusEvent<HTMLElement>): string | null => {
    const direct = (event.target as HTMLInputElement | null)?.value;
    if (typeof direct === "string") return direct;

    const nested = event.currentTarget?.querySelector("input") as HTMLInputElement | null;
    if (nested && typeof nested.value === "string") return nested.value;

    return null;
  }, []);

  const parseDateOrDateTime = React.useCallback((raw: string): Date | null => {
    const text = (raw || "")
      .replace(/\u200e/g, "")
      .replace(/_/g, "")
      .replace(/,/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!text) return null;

    const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{1,2}))?$/);
    if (!match) return null;

    const d = Number(match[1]);
    const m = Number(match[2]);
    const y = Number(match[3]);
    const hh = match[4] ? Number(match[4]) : 0;
    const mm = match[5] ? Number(match[5]) : 0;

    if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;

    const value = new Date(y, m - 1, d, hh, mm, 0, 0);
    if (Number.isNaN(value.getTime())) return null;
    if (value.getDate() !== d || value.getMonth() !== m - 1 || value.getFullYear() !== y) {
      return null;
    }

    return value;
  }, []);

  const parseDateOnly = React.useCallback((raw: string): Date | null => {
    const text = (raw || "")
      .replace(/\u200e/g, "")
      .replace(/_/g, "")
      .replace(/,/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!text) return null;

    const slashMatch = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      const d = Number(slashMatch[1]);
      const m = Number(slashMatch[2]);
      const y = Number(slashMatch[3]);
      const value = new Date(y, m - 1, d, 0, 0, 0, 0);
      if (Number.isNaN(value.getTime())) return null;
      if (value.getDate() !== d || value.getMonth() !== m - 1 || value.getFullYear() !== y) {
        return null;
      }
      return value;
    }

    const dashMatch = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (dashMatch) {
      const y = Number(dashMatch[1]);
      const m = Number(dashMatch[2]);
      const d = Number(dashMatch[3]);
      const value = new Date(y, m - 1, d, 0, 0, 0, 0);
      if (Number.isNaN(value.getTime())) return null;
      if (value.getDate() !== d || value.getMonth() !== m - 1 || value.getFullYear() !== y) {
        return null;
      }
      return value;
    }

    return null;
  }, []);

  const midnightReference = React.useMemo(() => {
    const value = new Date();
    value.setHours(0, 0, 0, 0);
    return value;
  }, []);

  const normalizePickerDate = React.useCallback((value: any): Date | null => {
    if (!value) return null;

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value?.toDate === "function") {
      const fromToDate = value.toDate();
      return fromToDate instanceof Date && !Number.isNaN(fromToDate.getTime())
        ? fromToDate
        : null;
    }

    if (value?.$d instanceof Date) {
      return Number.isNaN(value.$d.getTime()) ? null : value.$d;
    }

    if (typeof value === "string") {
      const fromString = new Date(value);
      return Number.isNaN(fromString.getTime()) ? null : fromString;
    }

    return null;
  }, []);

  const formatDateTimeText = React.useCallback((value: Date): string => {
    const dd = String(value.getDate()).padStart(2, "0");
    const mm = String(value.getMonth() + 1).padStart(2, "0");
    const yyyy = value.getFullYear();
    const hh = String(value.getHours()).padStart(2, "0");
    const min = String(value.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  }, []);

  const formatDateText = React.useCallback((value: Date): string => {
    const dd = String(value.getDate()).padStart(2, "0");
    const mm = String(value.getMonth() + 1).padStart(2, "0");
    const yyyy = value.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }, []);

  const currentUser = useSelector((state: any) => state.auth.user);
  const [currentEmployeeFromApi, setCurrentEmployeeFromApi] = React.useState<any | null>(null);
  const [missionSalesEmployees, setMissionSalesEmployees] = React.useState<Array<{ id: string; name: string; nickname?: string; avatar?: string | null; image1?: string | null; user?: { id?: string; name?: string }; user_id?: string }>>([]);
  const [missionSurveyEmployees, setMissionSurveyEmployees] = React.useState<Array<{ id: string; name: string; nickname?: string; avatar?: string | null; image1?: string | null; user?: { id?: string; name?: string }; user_id?: string }>>([]);
  const [missionCncEmployees, setMissionCncEmployees] = React.useState<Array<{ id: string; name: string; nickname?: string; avatar?: string | null; image1?: string | null; user?: { id?: string; name?: string }; user_id?: string }>>([]);

  const updateSurveyField = React.useCallback(
    (key: "survey_id" | "survey_date" | "survey_appointment_date" | "survey_notes", value: any) => {
      const normalizedValue = key.includes("date") ? normalizePickerDate(value) : value;
      onInputChange("survey", { [key]: normalizedValue });

      // Keep direct field update as fallback for legacy callers.
      onInputChange(key, normalizedValue);
    },
    [normalizePickerDate, onInputChange]
  );

  const normalizedEmployees = React.useMemo(() => {
    return (employees || []).map((employee: any) => {
      const fullName = [employee?.first_name, employee?.last_name].filter(Boolean).join(' ').trim();
      return {
        ...employee,
        name: employee?.name || employee?.user?.name || fullName || employee?.code || 'Nhân viên',
      };
    });
  }, [employees]);

  const currentEmployee = React.useMemo(() => {
    if (!currentUser?.id || !normalizedEmployees?.length) return null;
    return normalizedEmployees.find((employee: any) => {
      return employee?.user?.id === currentUser.id || employee?.user_id === currentUser.id;
    }) || null;
  }, [normalizedEmployees, currentUser?.id]);

  React.useEffect(() => {
    const loadCurrentEmployee = async () => {
      if (!currentUser?.id || currentEmployee?.id) {
        return;
      }

      const userId = String(currentUser.id);
      if (currentEmployeeByUserCache.has(userId)) {
        setCurrentEmployeeFromApi(currentEmployeeByUserCache.get(userId) || null);
        return;
      }

      const inflight = currentEmployeeByUserInFlight.get(userId);
      if (inflight) {
        const employee = await inflight;
        setCurrentEmployeeFromApi(employee || null);
        return;
      }

      const request = (async () => {
        try {
          const response = await getEmployeeByUser(userId);
          if (response?.status === 200 && response?.data?.id) {
            const employee = response.data as any;
            const fullName = [employee?.first_name, employee?.last_name].filter(Boolean).join(' ').trim();
            return {
              ...employee,
              name: employee?.name || employee?.user?.name || fullName || employee?.code || currentUser?.fullname || currentUser?.username || 'Nhân viên hiện tại',
            };
          }
          return null;
        } catch (error) {
          return null;
        }
      })();

      currentEmployeeByUserInFlight.set(userId, request);

      try {
        const employee = await request;
        currentEmployeeByUserCache.set(userId, employee);
        setCurrentEmployeeFromApi(employee);
      } finally {
        if (currentEmployeeByUserInFlight.get(userId) === request) {
          currentEmployeeByUserInFlight.delete(userId);
        }
      }
    };

    loadCurrentEmployee();
  }, [currentUser?.id, currentUser?.fullname, currentUser?.username, currentEmployee?.id]);

  const employeesForDropdown = React.useMemo(() => {
    if (!currentEmployeeFromApi?.id) {
      return normalizedEmployees;
    }

    const exists = normalizedEmployees.some((employee: any) => employee?.id === currentEmployeeFromApi.id);
    if (exists) {
      return normalizedEmployees;
    }

    return [currentEmployeeFromApi, ...normalizedEmployees];
  }, [normalizedEmployees, currentEmployeeFromApi]);

  React.useEffect(() => {
    const extractUniqueEmployees = (employees: any[]) => {
      const uniqueEmployees = new Map<string, { id: string; name: string; nickname?: string; avatar?: string | null; image1?: string | null; user?: { id?: string; name?: string }; user_id?: string }>();
      (employees || []).forEach((employee: any) => {
        const employeeId = employee?.id || '';
        const fullName = [employee?.first_name, employee?.last_name].filter(Boolean).join(' ').trim();
        const employeeNickname = typeof employee?.nickname === 'string' ? employee.nickname : '';
        const employeeName = employee?.name || employee?.user?.name || fullName || employee?.code || '';
        const employeeAvatar = employee?.user?.avatar || null;
        const employeeImage1 = employee?.image1 || null;
        if (employeeId && employeeName && !uniqueEmployees.has(employeeId)) {
          uniqueEmployees.set(employeeId, {
            id: employeeId,
            name: employeeName,
            nickname: employeeNickname,
            avatar: employeeAvatar,
            image1: employeeImage1,
            user: employee?.user,
            user_id: employee?.user_id,
          });
        }
      });
      return Array.from(uniqueEmployees.values());
    };

    const loadMissionEmployees = async () => {
      const companyId = formData.company_id || getCurrentCompanyId() || '';
      if (!companyId) {
        setMissionSalesEmployees([]);
        setMissionSurveyEmployees([]);
        setMissionCncEmployees([]);
        return;
      }

      const cached = missionEmployeesCache.get(companyId);
      if (cached) {
        setMissionSalesEmployees(cached.sales);
        setMissionSurveyEmployees(cached.survey);
        setMissionCncEmployees(cached.cnc);
        return;
      }

      const inflight = missionEmployeesInFlight.get(companyId);
      if (inflight) {
        const payload = await inflight;
        setMissionSalesEmployees(payload.sales);
        setMissionSurveyEmployees(payload.survey);
        setMissionCncEmployees(payload.cnc);
        return;
      }

      const request = (async (): Promise<MissionEmployeesPayload> => {
        const [banHangMissions, khaoSatMissions, cncMissions] = await Promise.all([
           getEmployees({ company: companyId, mission: 'BAN_HANG', no_pagination: true }).then(r => r.data?.results || (Array.isArray(r.data) ? r.data : [])),
           getEmployees({ company: companyId, mission: 'KHAO_SAT', no_pagination: true }).then(r => r.data?.results || (Array.isArray(r.data) ? r.data : [])),
           getEmployees({ company: companyId, mission: 'CNC', no_pagination: true }).then(r => r.data?.results || (Array.isArray(r.data) ? r.data : [])),
        ]);

        return {
          sales: extractUniqueEmployees(filterEmployeesByMission(banHangMissions, 'BAN_HANG')),
          survey: extractUniqueEmployees(filterEmployeesByMission(khaoSatMissions, 'KHAO_SAT')),
          cnc: extractUniqueEmployees(filterEmployeesByMission(cncMissions, 'CNC')),
        };
      })();

      missionEmployeesInFlight.set(companyId, request);

      try {
        const payload = await request;
        missionEmployeesCache.set(companyId, payload);
        setMissionSalesEmployees(payload.sales);
        setMissionSurveyEmployees(payload.survey);
        setMissionCncEmployees(payload.cnc);
      } catch (error) {
        setMissionSalesEmployees([]);
        setMissionSurveyEmployees([]);
        setMissionCncEmployees([]);
      } finally {
        if (missionEmployeesInFlight.get(companyId) === request) {
          missionEmployeesInFlight.delete(companyId);
        }
      }
    };

    loadMissionEmployees();
  }, [formData.company_id]);

  const salesEmployeesForDropdown = React.useMemo(() => {
    const sourceEmployees = missionSalesEmployees.length
      ? missionSalesEmployees
      : filterEmployeesByMission(employeesForDropdown as any[], 'BAN_HANG');

    const byId = new Map<string, any>();
    sourceEmployees.forEach((employee) => {
      byId.set(employee.id, employee);
    });

    // Keep selected value visible if not in mission result yet.
    const selectedId = formData.sales_employee_id;
    if (selectedId) {
      const selectedFromAll = employeesForDropdown.find((employee: any) => employee.id === selectedId);
      if (selectedFromAll && !byId.has(selectedId)) {
        byId.set(selectedId, selectedFromAll);
      }
    }

    return Array.from(byId.values());
  }, [missionSalesEmployees, employeesForDropdown, formData.sales_employee_id]);

  const surveyEmployeesForDropdown = React.useMemo(() => {
    const sourceEmployees = missionSurveyEmployees.length
      ? missionSurveyEmployees
      : filterEmployeesByMission(employeesForDropdown as any[], 'KHAO_SAT');

    const byId = new Map<string, any>();
    sourceEmployees.forEach((employee) => {
      byId.set(employee.id, employee);
    });

    const selectedId = formData.survey_id || '';
    if (selectedId) {
      const selectedFromAll = employeesForDropdown.find((employee: any) => employee.id === selectedId);
      if (selectedFromAll && !byId.has(selectedId)) {
        byId.set(selectedId, selectedFromAll);
      }
    }

    return Array.from(byId.values());
  }, [missionSurveyEmployees, employeesForDropdown, formData.survey_id]);

  const cncEmployeesForDropdown = React.useMemo(() => {
    const sourceEmployees = missionCncEmployees.length
      ? missionCncEmployees
      : filterEmployeesByMission(employeesForDropdown as any[], 'CNC');

    const byId = new Map<string, any>();
    sourceEmployees.forEach((employee) => {
      byId.set(employee.id, employee);
    });

    const selectedId = formData.tech_cnc_id || '';
    if (selectedId) {
      const selectedFromAll = employeesForDropdown.find((employee: any) => employee.id === selectedId);
      if (selectedFromAll && !byId.has(selectedId)) {
        byId.set(selectedId, selectedFromAll);
      }
    }

    return Array.from(byId.values());
  }, [missionCncEmployees, employeesForDropdown, formData.tech_cnc_id]);

  const effectiveCurrentEmployee = currentEmployee || currentEmployeeFromApi;
  const preferredSalesEmployeeId = React.useMemo(() => {
    const currentUserId = currentUser?.id ? String(currentUser.id) : '';
    if (!currentUserId) return '';

    const userMatched = missionSalesEmployees.find((employee: any) => {
      const userId = employee?.user?.id ? String(employee.user.id) : '';
      const directUserId = employee?.user_id ? String(employee.user_id) : '';
      return userId === currentUserId || directUserId === currentUserId;
    });
    if (userMatched?.id) return userMatched.id;

    if (
      effectiveCurrentEmployee?.id &&
      missionSalesEmployees.some((employee: any) => employee?.id === effectiveCurrentEmployee.id)
    ) {
      return effectiveCurrentEmployee.id;
    }

    return '';
  }, [missionSalesEmployees, currentUser?.id, effectiveCurrentEmployee?.id]);

  const preferredSurveyEmployeeId = React.useMemo(() => {
    const currentUserId = currentUser?.id ? String(currentUser.id) : '';
    if (!currentUserId) return '';

    const userMatched = missionSurveyEmployees.find((employee: any) => {
      const userId = employee?.user?.id ? String(employee.user.id) : '';
      const directUserId = employee?.user_id ? String(employee.user_id) : '';
      return userId === currentUserId || directUserId === currentUserId;
    });
    if (userMatched?.id) return userMatched.id;

    if (
      effectiveCurrentEmployee?.id &&
      missionSurveyEmployees.some((employee: any) => employee?.id === effectiveCurrentEmployee.id)
    ) {
      return effectiveCurrentEmployee.id;
    }

    return '';
  }, [missionSurveyEmployees, currentUser?.id, effectiveCurrentEmployee?.id]);

  const preferredCncEmployeeId = React.useMemo(() => {
    const currentUserId = currentUser?.id ? String(currentUser.id) : '';
    if (!currentUserId) return '';

    const userMatched = missionCncEmployees.find((employee: any) => {
      const userId = employee?.user?.id ? String(employee.user.id) : '';
      const directUserId = employee?.user_id ? String(employee.user_id) : '';
      return userId === currentUserId || directUserId === currentUserId;
    });
    if (userMatched?.id) return userMatched.id;

    if (
      effectiveCurrentEmployee?.id &&
      missionCncEmployees.some((employee: any) => employee?.id === effectiveCurrentEmployee.id)
    ) {
      return effectiveCurrentEmployee.id;
    }

    return '';
  }, [missionCncEmployees, currentUser?.id, effectiveCurrentEmployee?.id]);

  React.useEffect(() => {
    if (isEditMode) {
      return;
    }

    if (formData.sales_employee_id) {
      return;
    }

    if (preferredSalesEmployeeId) {
      onInputChange("sales_employee_id", preferredSalesEmployeeId);
    }
  }, [isEditMode, formData.sales_employee_id, preferredSalesEmployeeId, onInputChange]);

  React.useEffect(() => {
    if (isEditMode) {
      return;
    }

    if (formData.survey_id) {
      return;
    }

    if (preferredSurveyEmployeeId) {
      onInputChange("survey_id", preferredSurveyEmployeeId);
    }
  }, [isEditMode, formData.survey_id, preferredSurveyEmployeeId, onInputChange]);

  React.useEffect(() => {
    if (isEditMode) {
      return;
    }

    if (formData.tech_cnc_id) {
      return;
    }

    if (preferredCncEmployeeId) {
      onInputChange("tech_cnc_id", preferredCncEmployeeId);
    }
  }, [isEditMode, formData.tech_cnc_id, preferredCncEmployeeId, onInputChange]);

  const renderEmployeeAutocomplete = (
    label: string,
    value: string,
    options: any[],
    onChange: (newValue: string) => void,
    placeholder: string,
  ) => (
    <LabeledField label={label} labelWidth={130}>
      <Autocomplete
        size="small"
        fullWidth
        options={options}
        value={options.find((opt) => opt.id === value) || null}
        onChange={(_event, newValue) => onChange(newValue?.id || '')}
        getOptionLabel={(option) => option.nickname || option.name || ''}
        isOptionEqualToValue={(option, val) => option.id === val?.id}
        disabled={disabled}
        noOptionsText="Không có dữ liệu nhân viên"
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            fullWidth
            placeholder={placeholder}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: disabled ? '#f8fafc' : '#fff',
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <UserOptionDisplay
              user={{
                id: option.id,
                name: option.name,
                nickname: option.nickname,
                avatar: option.avatar || option.user?.avatar || null,
                image1: option.image1 || null,
              }}
            />
          </Box>
        )}
      />
    </LabeledField>
  );

  return (
    <Box
      sx={{
        ...sharedFormStyles.noteSectionWrapper,
        mt: 2,
        p: { xs: 1.5, md: 2 },
        border: '1px solid #e5e7eb',
        borderRadius: 2,
        backgroundColor: '#ffffff',
      }}
    >
      <Typography sx={{ ...sharedFormStyles.sectionTitle, mb: 1.5 }}>
        Thông tin nhân viên
      </Typography>

      <Box
        sx={{
          border: '1px solid #eef2f7',
          borderRadius: 1.5,
          p: { xs: 1.25, md: 1.5 },
          mb: 1.5,
          backgroundColor: '#f8fafc',
        }}
      >
      <Grid container rowSpacing={1.5} columnSpacing={3}>
        {/* Sales Employee & Delivery Date */}
        <Grid size={{ xs: 12, md: 6 }}>
          {renderEmployeeAutocomplete(
            "Nhân viên bán hàng",
            formData.sales_employee_id,
            salesEmployeesForDropdown,
            (newValue) => onInputChange("sales_employee_id", newValue),
            "Chọn nhân viên"
          )}
        </Grid>
      
        <Grid size={{ xs: 12, md: 6 }}>
          <LabeledField label="Ngày hẹn giao hàng" labelWidth={130}>
            <DatePicker
              value={formData.delivery_date}
              onChange={(date) => onInputChange("delivery_date", date)}
              format="dd/MM/yyyy"
              disabled={disabled}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: "Chọn ngày hẹn giao hàng",
                  sx: sharedFormStyles.datePickerArrowAlign,
                  InputProps: {
                    sx: {
                      borderRadius: 1,
                      backgroundColor: disabled ? '#f8fafc' : '#fff',
                    },
                  },
                  disabled: disabled,
                },
              }}
            />
          </LabeledField>
        </Grid>
      </Grid>
      </Box>

      <Box
        sx={{
          border: '1px solid #eef2f7',
          borderRadius: 1.5,
          p: { xs: 1.25, md: 1.5 },
          mb: 1.5,
          backgroundColor: '#f8fafc',
        }}
      >
      <Grid container rowSpacing={1.5} columnSpacing={3}> 
        {/* Survey Employee & Survey Date */}
        <Grid size={{ xs: 12, md: 4 }}>
          {renderEmployeeAutocomplete(
            "Khảo sát",
            formData.survey_id || "",
            surveyEmployeesForDropdown,
            (newValue) => updateSurveyField("survey_id", newValue),
            "Chọn nhân viên khảo sát"
          )}
        </Grid>
        
        <Grid size={{ xs: 12, md: 4 }}>
          <LabeledField label="Ngày hẹn KS" labelWidth={130} fieldAlign="start">
            <DateTimePicker
              value={normalizePickerDate(formData.survey_appointment_date)}
              referenceDate={midnightReference}
              onChange={(date) => {
                const normalized = normalizePickerDate(date);
                if (!normalized) return;
                onInputChange("survey_appointment_date", normalized);
                onInputChange("survey_appointment_date_text", formatDateTimeText(normalized));
                onInputChange("survey", { survey_appointment_date: normalized });
              }}
              onAccept={(date) => {
                const normalized = normalizePickerDate(date);
                if (!normalized) return;
                onInputChange("survey_appointment_date", normalized);
                onInputChange("survey_appointment_date_text", formatDateTimeText(normalized));
                onInputChange("survey", { survey_appointment_date: normalized });
              }}
              format="dd/MM/yyyy HH:mm"
              ampm={false}
              disabled={disabled}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: "Chọn ngày hẹn khảo sát",
                  sx: sharedFormStyles.datePickerArrowAlign,
                  InputProps: {
                    sx: {
                      borderRadius: 1,
                      backgroundColor: disabled ? '#f8fafc' : '#fff',
                    },
                  },
                  disabled: disabled,
                  onBlur: (event: React.FocusEvent<HTMLElement>) => {
                    const rawValue = getBlurInputValue(event);
                    if (rawValue == null) return;

                    const raw = rawValue.trim();
                    onInputChange("survey_appointment_date_text", rawValue);
                    onInputChange("survey", { survey_appointment_date_text: rawValue });
                    if (!raw) {
                      onInputChange("survey_appointment_date", null);
                      onInputChange("survey", { survey_appointment_date: null });
                      return;
                    }

                    // If picker already resolved a Date (with user-edited time), do not overwrite it.
                    if (formData.survey_appointment_date) {
                      return;
                    }

                    const parsed = parseDateOrDateTime(raw);
                    if (parsed) {
                      onInputChange("survey_appointment_date", parsed);
                      onInputChange("survey", { survey_appointment_date: parsed });
                    }
                  },
                },
              }}
            />
          </LabeledField>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <LabeledField label="Ngày KS" labelWidth={130} fieldAlign="start">
            <DateTimePicker
              value={normalizePickerDate(formData.survey_date)}
              referenceDate={midnightReference}
              onChange={(date) => {
                const normalized = normalizePickerDate(date);
                if (!normalized) return;
                onInputChange("survey_date", normalized);
                onInputChange("survey_date_text", formatDateTimeText(normalized));
                onInputChange("survey", { survey_date: normalized });
              }}
              onAccept={(date) => {
                const normalized = normalizePickerDate(date);
                if (!normalized) return;
                onInputChange("survey_date", normalized);
                onInputChange("survey_date_text", formatDateTimeText(normalized));
                onInputChange("survey", { survey_date: normalized });
              }}
              format="dd/MM/yyyy HH:mm"
              ampm={false}
              disabled={disabled}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: "Chọn ngày KS",
                  sx: sharedFormStyles.datePickerArrowAlign,
                  InputProps: {
                    sx: {
                      borderRadius: 1,
                      backgroundColor: disabled ? '#f8fafc' : '#fff',
                    },
                  },
                  disabled: disabled,
                  onBlur: (event: React.FocusEvent<HTMLElement>) => {
                    const rawValue = getBlurInputValue(event);
                    if (rawValue == null) return;

                    const raw = rawValue.trim();
                    onInputChange("survey_date_text", rawValue);
                    onInputChange("survey", { survey_date_text: rawValue });
                    if (!raw) {
                      onInputChange("survey_date", null);
                      onInputChange("survey", { survey_date: null });
                      return;
                    }

                    // If picker already resolved a Date (with user-edited time), do not overwrite it.
                    if (formData.survey_date) {
                      return;
                    }

                    const parsed = parseDateOrDateTime(raw);
                    if (parsed) {
                      onInputChange("survey_date", parsed);
                      onInputChange("survey", { survey_date: parsed });
                    }
                  },
                },
              }}
            />
          </LabeledField>
        </Grid>
         
        <Grid size={{ xs: 12, md: 12 }}>
          <LabeledField
            label="Ghi chú KS"
            labelWidth={130}
            fieldAlign="start"
            labelPosition="top"
            multiline
          >
            <TextareaAutosize
              value={formData.survey_notes || ''}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                if (disabled) return;
                updateSurveyField("survey_notes", e.target.value);
              }}
              spellCheck={false}
              autoComplete="off"
              readOnly={disabled}
              minRows={3}
              style={sharedFormStyles.noteTextarea(!disabled)}
              onFocus={(e) => {
                if (!disabled) {
                  e.target.style.borderColor = '#3b82f6';
                }
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
              }}
            />
          </LabeledField>
        </Grid>

      </Grid>
      </Box>

      <Box
        sx={{
          border: '1px solid #eef2f7',
          borderRadius: 1.5,
          p: { xs: 1.25, md: 1.5 },
          backgroundColor: '#f8fafc',
        }}
      >
      <Grid container rowSpacing={1.5} columnSpacing={3}> 
          <Grid size={{ xs: 12, md: 6 }}>
            {renderEmployeeAutocomplete(
              "Kỹ thuật CNC",
              formData.tech_cnc_id || "",
              cncEmployeesForDropdown,
              (newValue) => onInputChange("tech_cnc_id", newValue),
              "Chọn nhân viên kỹ thuật CNC"
            )}
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <LabeledField label="Ngày xuất file" labelWidth={130} fieldAlign="start">
            <DatePicker
              value={normalizePickerDate(formData.export_file_date)}
              onChange={(date) => {
                const normalized = normalizePickerDate(date);
                if (!normalized) {
                  onInputChange("export_file_date", null);
                  onInputChange("export_file_date_text", "");
                  return;
                }
                onInputChange("export_file_date", normalized);
                onInputChange("export_file_date_text", formatDateText(normalized));
              }}
              onAccept={(date) => {
                const normalized = normalizePickerDate(date);
                if (!normalized) {
                  onInputChange("export_file_date", null);
                  onInputChange("export_file_date_text", "");
                  return;
                }
                onInputChange("export_file_date", normalized);
                onInputChange("export_file_date_text", formatDateText(normalized));
              }}
              format="dd/MM/yyyy"
              disabled={disabled}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: "Chọn ngày xuất file",
                  sx: sharedFormStyles.datePickerArrowAlign,
                  InputProps: {
                    sx: {
                      borderRadius: 1,
                      backgroundColor: disabled ? '#f8fafc' : '#fff',
                    },
                  },
                  disabled: disabled,
                  onBlur: (event: React.FocusEvent<HTMLElement>) => {
                    const rawValue = getBlurInputValue(event);
                    if (rawValue == null) return;

                    const raw = rawValue.trim();
                    onInputChange("export_file_date_text", rawValue);
                    if (!raw) {
                      onInputChange("export_file_date", null);
                      return;
                    }

                    const parsed = parseDateOnly(raw);
                    if (parsed) {
                      onInputChange("export_file_date", parsed);
                      onInputChange("export_file_date_text", formatDateText(parsed));
                    }
                  },
                },
              }}
            />
          </LabeledField>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
}
